import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { Input, Table, Select } from 'antd'
import moment, { Moment } from 'moment-timezone'
import shallow from 'zustand/shallow'
import PerFlexRow from '../FlexRow'
import PerInputNumber from '../PerInputNumber'
import PerDatePicker from '../DatePicker'
import { PerEmpty } from '../Empty'
import { useAuth } from '../../../context/auth-context'
import { EmployeeLevelEnum } from '../../../types'
import { useForecastModalStore, ForecastModalHiringsTableErrors, ForecastModalHiringsTableValues, ForecastModalNewHiring } from '.'
import { useEditForecastPermission } from '../../../modules/pyramids/usePermission'
import { usePyramidsStore } from '../../../modules/pyramids/PyramidsStore'

const { Option } = Select

export const ForecastModalHiringsTable: FC<unknown> = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [setHiringsTableForm, newHiringsData, hiringsSelectedRows, setHiringsSelectedRows, filterBu, filterEmployeeLevels] =
    useForecastModalStore(
      (state) => [
        state.setHiringsTableForm,
        state.newHiringsData,
        state.hiringsSelectedRows,
        state.setHiringsSelectedRows,
        state.filterBu,
        state.filterEmployeeLevels,
      ],
      shallow,
    )

  const [companyId, isAgePyramidVisible] = usePyramidsStore((state) => [state.selected.companyId, state.isAgePyramidVisible], shallow)

  const hasPermission = useEditForecastPermission(companyId)

  const hiringsTableForm = useForm<ForecastModalHiringsTableValues>({
    defaultValues: {
      hirings: newHiringsData,
    },
  })

  const {
    control,
    formState: { errors },
  } = hiringsTableForm

  const { fields } = useFieldArray({
    name: 'hirings',
    keyName: 'key',
    control,
  })

  const rowSelection = {
    onChange: (_rowIds, rows) => {
      setHiringsSelectedRows(rows)
    },
    selectedRowKeys: hiringsSelectedRows.map((row: ForecastModalNewHiring) => row.key),
    getCheckboxProps: () => ({
      disabled: !hasPermission,
    }),
  }

  const filterBuData = [{ id: undefined, name: undefined, isExpired: false }, ...filterBu].map((bu, index) => (
    <Option key={bu.id || `key${index}`} value={bu.id || null} className={bu.isExpired ? 'bu-expired' : undefined}>
      {!!bu.name && <span>{bu.name}</span>}
      {!bu.name && <span>-</span>}
    </Option>
  ))

  const employeeLevelData = filterEmployeeLevels
    .filter((level: EmployeeLevelEnum) => level !== EmployeeLevelEnum.Leaver)
    .map((level: EmployeeLevelEnum) => (
      <Option key={level} value={level}>
        {level}
      </Option>
    ))

  useEffect(() => {
    setHiringsTableForm(hiringsTableForm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasError = (errors: ForecastModalHiringsTableErrors, fieldName: string, index: number) => {
    if (!!errors && !!errors.hirings && index < errors.hirings.length) {
      return !!errors.hirings[index] && !!errors.hirings[index][fieldName]
    }
    return false
  }

  const columns = [
    {
      key: 'forecast-hiring-name',
      title: t('pyramidsView.forecastModal.table.name'),
      className: 'minWidth-column-xl',
      render: (_: ForecastModalNewHiring, _record: ForecastModalNewHiring, index: number) => {
        return (
          <Controller
            name={`hirings.${index}.newHiringFullName`}
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                value={value || undefined}
                onChange={onChange}
                onBlur={onBlur}
                allowClear
                width={'100%'}
                className={hasError(errors as ForecastModalHiringsTableErrors, 'newHiringFullName', index) ? 'has-error' : ''}
                placeholder={t('pyramidsView.forecastModal.table.placeholders.name')}
                disabled={!hasPermission}
              />
            )}
          />
        )
      },
    },
    {
      key: 'forecast-hiring-bu',
      title: t('pyramidsView.forecastModal.table.bu'),
      className: 'minWidth-column-s',
      render: (_: ForecastModalNewHiring, _record: ForecastModalNewHiring, index: number) => {
        return (
          <Controller
            name={`hirings.${index}.businessUnitId`}
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                onChange={onChange}
                value={value}
                placeholder={t('pyramidsView.forecastModal.table.placeholders.bu')}
                disabled={!hasPermission}
                dropdownMatchSelectWidth={false}
              >
                {filterBuData}
              </Select>
            )}
          />
        )
      },
    },
    {
      key: 'forecast-hiring-salary',
      title: t('pyramidsView.forecastModal.table.salary'),
      className: 'minWidth-column-m textAlign-right',
      render: (_: ForecastModalNewHiring, _record: ForecastModalNewHiring, index: number) => {
        return (
          <Controller
            name={`hirings.${index}.salary`}
            control={control}
            render={({ field: { onChange, value } }) => (
              <PerInputNumber
                type="currency"
                value={value || undefined}
                onChange={onChange}
                width={'100%'}
                currency={user.currencyForPayments}
                placeholder={t('pyramidsView.forecastModal.table.placeholders.salary')}
                currencyPlaceholder
                disabled={!hasPermission}
                timezoneAndLocale={user}
              />
            )}
          />
        )
      },
    },
    {
      key: 'forecast-hiring-bonus',
      title: t('pyramidsView.forecastModal.table.bonus'),
      className: 'minWidth-column-m textAlign-right',
      render: (_: ForecastModalNewHiring, _record: ForecastModalNewHiring, index: number) => {
        return (
          <Controller
            name={`hirings.${index}.bonus`}
            control={control}
            render={({ field: { onChange, value } }) => (
              <PerInputNumber
                type="currency"
                value={value || undefined}
                onChange={onChange}
                width={'100%'}
                currency={user.currencyForPayments}
                placeholder={t('pyramidsView.forecastModal.table.placeholders.bonus')}
                currencyPlaceholder
                disabled={!hasPermission}
                timezoneAndLocale={user}
              />
            )}
          />
        )
      },
    },
    {
      key: 'forecast-hiring-age',
      title: t('pyramidsView.forecastModal.table.age'),
      className: 'minWidth-column-s',
      render: (_: ForecastModalNewHiring, _record: ForecastModalNewHiring, index: number) => {
        return (
          <Controller
            name={`hirings.${index}.age`}
            control={control}
            render={({ field: { onChange, value } }) => (
              <PerInputNumber
                value={value || undefined}
                onChange={onChange}
                onClick={(e) => {
                  e.currentTarget.setSelectionRange(0, e.currentTarget.value.length)
                }}
                min={1}
                max={99}
                maxLength={2}
                placeholder={t('pyramidsView.forecastModal.table.placeholders.age')}
                parser={(value) => {
                  if (!!value && !Number(value)) {
                    return 1
                  } else if (!!value && Number(value) > 99) {
                    return 99
                  }
                  return value || ''
                }}
                disabled={!hasPermission}
              />
            )}
          />
        )
      },
    },
    {
      key: 'forecast-hiring-level',
      title: t('pyramidsView.forecastModal.table.level'),
      className: 'minWidth-column-s',
      render: (_: ForecastModalNewHiring, _record: ForecastModalNewHiring, index: number) => {
        return (
          <Controller
            name={`hirings.${index}.levelChange`}
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value } }) => {
              return (
                <Select
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  placeholder={t('pyramidsView.forecastModal.common.selectLevel')}
                  className={hasError(errors as ForecastModalHiringsTableErrors, 'levelChange', index) ? 'has-error' : ''}
                  disabled={!hasPermission}
                >
                  {employeeLevelData}
                </Select>
              )
            }}
          />
        )
      },
    },
    {
      key: 'forecast-hiring-levelChange',
      className: 'minWidth-column-l',
      title: t('pyramidsView.forecastModal.table.dateofLevelChange'),
      render: (_: ForecastModalNewHiring, _record: ForecastModalNewHiring, index: number) => {
        return (
          <Controller
            name={`hirings.${index}.dateOfLevelChange`}
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value } }) => (
              <PerDatePicker
                showToday={false}
                inputDate={value || ''}
                dateFormat="DMY"
                timeZoneAndLocale={user}
                size="default"
                width={'100%'}
                disabledDate={(current: Moment | null) => !!current && current < moment()}
                className={hasError(errors as ForecastModalHiringsTableErrors, 'dateOfLevelChange', index) ? 'has-error' : ''}
                onDateChanged={(date) => onChange(date)}
                onBlur={onBlur}
                disabled={!hasPermission}
              />
            )}
          />
        )
      },
    },
  ].filter((column) => column.key !== 'forecast-hiring-age' || isAgePyramidVisible)

  return (
    <PerFlexRow className="fullWidth" mt="l">
      <Table
        columns={columns}
        dataSource={fields}
        rowSelection={rowSelection}
        rowClassName={() => 'minHeight-row'}
        rowKey={'key'}
        pagination={false}
        className="fullWidth"
        locale={{
          emptyText: (
            <PerEmpty heading={t('pyramidsView.forecastModal.common.noHirings')} icon="employeesNo_stroke" iconColor="secondary-lighter" />
          ),
        }}
      />
    </PerFlexRow>
  )
}
